import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type { HostedAgentLinkCreateInput, HostedAgentLinkPatch } from "@/api/admin-api"

import {
  createHostedAgentLink,
  deleteHostedAgentLink,
  listHostedAgentLinks,
  updateHostedAgentLink,
} from "@/api/admin-api"

import { FAST_STALE_TIME, GC_TIME } from "./_shared"

// Agent links (B3 map) — rooted under the hosted-agents domain so the generic
// hosted-agent invalidation (agent delete etc.) also refreshes the map, while
// link mutations only need to touch the links key + the affected agent.
const HOSTED_AGENTS_KEY = ["admin", "hosted-agents"] as const
const HOSTED_AGENT_LINKS_KEY = [...HOSTED_AGENTS_KEY, "links"] as const

export function useAdminHostedAgentLinksQuery(enabled = true) {
  return useQuery({
    queryKey: HOSTED_AGENT_LINKS_KEY,
    queryFn: listHostedAgentLinks,
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
  })
}

function useInvalidateLinks() {
  const queryClient = useQueryClient()
  return async (fromAgentId: string) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: HOSTED_AGENT_LINKS_KEY }),
      // The link lives in the from-agent's document → its revision moved.
      queryClient.invalidateQueries({ queryKey: [...HOSTED_AGENTS_KEY, fromAgentId] }),
      queryClient.invalidateQueries({ queryKey: HOSTED_AGENTS_KEY, exact: true }),
    ])
  }
}

export function useCreateHostedAgentLinkMutation() {
  const invalidate = useInvalidateLinks()
  return useMutation({
    mutationFn: ({
      fromAgentId,
      input,
    }: {
      fromAgentId: string
      input: HostedAgentLinkCreateInput
    }) => createHostedAgentLink(fromAgentId, input),
    onSuccess: (_link, { fromAgentId }) => invalidate(fromAgentId),
  })
}

export function useUpdateHostedAgentLinkMutation() {
  const invalidate = useInvalidateLinks()
  return useMutation({
    mutationFn: ({
      fromAgentId,
      linkId,
      patch,
    }: {
      fromAgentId: string
      linkId: string
      patch: HostedAgentLinkPatch
    }) => updateHostedAgentLink(fromAgentId, linkId, patch),
    onSuccess: (_link, { fromAgentId }) => invalidate(fromAgentId),
  })
}

export function useDeleteHostedAgentLinkMutation() {
  const invalidate = useInvalidateLinks()
  return useMutation({
    mutationFn: ({ fromAgentId, linkId }: { fromAgentId: string; linkId: string }) =>
      deleteHostedAgentLink(fromAgentId, linkId),
    onSuccess: (_void, { fromAgentId }) => invalidate(fromAgentId),
  })
}
